import { createElement } from 'react';
import { Divider } from 'ui/divider';
import { FormField } from 'ui/formField';
import { InputText } from 'ui/inputs';
import { spacing } from 'ui/theme';
import { Toggle } from 'ui/toggle';
import { HelpTooltip } from 'ui/tooltip';
import * as t from '../../utils/widgetSettingsTypes';
import { Label, StyledSelect, StyledTextArea } from './SettingsPanel.styled';

// type Props = {
//   /** Metric of the widget. */
//   metric: {},
//   /** Name of widget */
//   name: string,
//   /** Callback function when config is changed. */
//   onChangeSettings: () => void,
//   /** Callback function when name is changed. */
//   onChangeName: () => void,
//   /** Callback function when markdown editor is scrolled. */
//   onScrollMarkdown: () => void,
//   /** Settings data */
//   settings: {},
//   /** List of settings containing getValue function, label, and onChange function. */
//   settingsList: [],
// };

export const SettingsPanel = ({
  metric,
  name,
  onChangeSettings = () => {},
  onChangeName = () => {},
  onScrollMarkdown = () => {},
  settings,
  settingsList = [],
}) => {
  /**
   * handleChangeSettings
   * Callback function when settings is changed.
   * @param { Object } e: The change event.
   * @param { Object } newSettings: The updated settings.
   * @return { void }
   */
  const handleChangeSettings = (e, newSettings) => {
    onChangeSettings(e, Object.assign({}, newSettings));
  };

  /**
   * handleChangeName
   * Callback function when name is changed.
   * @param { string } value: The updated name value.
   * @return { void }
   */
  const handleChangeName = (value) => {
    onChangeName({}, value);
  };

  /**
   * Additional input forms to add from settingsList
   */
  const additionalForms = settingsList.map(
    (
      {
        appendDivider,
        getInput,
        getValue,
        helpText,
        label,
        onChange,
        options,
        prependDivider,
        props: configProps,
        shouldDisplay,
        type,
      },
      index,
    ) => {
      /**
       * Wraps onChange with handleChangeSettings and returns new function.
       * @param { String } valueProp: The prop mapping to value.
       * @returns {Function} Wrapped onChange function to trigger state change.
       */
      const getChangeFunction =
        (valueProp = 'value') =>
        (e, inputRef) =>
          handleChangeSettings(e, onChange(inputRef[valueProp], settings));

      const handleSelect = (newOptions) =>
        handleChangeSettings({}, onChange(newOptions[0].value, settings));

      const handleTextChange = (newValue) => handleChangeSettings({}, onChange(newValue, settings));

      /* Determines if config is shown. Default true is showIf function is missing. */
      const displayConfig = shouldDisplay ? shouldDisplay({ metric, ...settings }) : true;
      /* The value of the input from getValue function. */
      const value = getValue(settings);
      let input = '';

      switch (type) {
        case t.CUSTOM:
          // Custom inputs will recieve wrapped onChange function, value and other props.
          input = getInput({
            onChange: getChangeFunction(),
            value,
            settings,
            ...configProps,
          });
          break;
        case t.DIVIDER:
          input = <Divider />;
          break;
        case t.DROPDOWN: {
          const mappedOptions = options.map((option) => ({
            ...option,
            label: (
              <span className="dashboard-select__option-label">
                {option.icon && createElement(option.icon)}
                {option.text}
              </span>
            ),
          }));
          input = (
            <StyledSelect
              {...configProps}
              classNamePrefix="dashboard-select"
              isSearchable={false}
              menuPlacement="top"
              onSelect={handleSelect}
              options={mappedOptions}
              value={mappedOptions.find((option) => option.value === value)}
            />
          );
          break;
        }
        case t.TOGGLE:
          input = (
            <Toggle
              isActive={!!value}
              onClick={(isActive) => handleChangeSettings({}, onChange(isActive, settings))}
            />
          );
          break;
        case t.TEXTAREA:
          input = <StyledTextArea onChange={handleTextChange} value={value} />;
          break;
        case t.MARKDOWN:
          input = (
            <StyledTextArea
              $isMarkdownArea={true}
              onChange={handleTextChange}
              onScroll={onScrollMarkdown}
              value={value}
            />
          );
          break;
        default:
          input = (
            <InputText
              data-test={`settings-panel-${label?.toLowerCase().replace(' ', '-')}-input`}
              onChange={handleTextChange}
              value={value}
            />
          );
      }

      if (displayConfig) {
        return (
          <FormField key={`${label}${index}`}>
            {prependDivider && <Divider />}
            {label && (
              <Label>
                {label}
                <HelpTooltip
                  isEnabled={helpText}
                  tooltipContent={helpText}
                  iconProps={{ height: 20, width: 20 }}
                />
              </Label>
            )}
            {input}
            {appendDivider && <Divider />}
          </FormField>
        );
      }
      return '';
    },
  );

  return (
    <div>
      <FormField
        labelText={
          <span style={{ display: 'flex', gap: spacing.quarter, alignItems: 'center' }}>
            Name
            <HelpTooltip
              iconProps={{ height: 20, width: 20 }}
              tooltipContent="Optional name for the widget"
            />
          </span>
        }
      >
        <InputText
          data-test="settings-panel-widget-name-input"
          placeholder="Widget name"
          onChange={handleChangeName}
          value={name}
        />
      </FormField>
      {additionalForms}
    </div>
  );
};
